import { getMenu } from "../../services/MenuService";
import { MODULE_ICON_MAP, DEFAULT_MODULE_ICON } from "../../config/menuFrontendConfig";
import { SET_MENU_DATA, CLEAR_MENU_DATA } from "../action-types";

export { SET_MENU_DATA, CLEAR_MENU_DATA };

// Transforms a page object into the shape Sidebar expects.
// Accepts both: static config pages (pageAccessKey already set)
// and future API pages (pageCode field from backend).
function normalizePage(page, idx) {
  return {
    id:            page.id,
    name:          page.name,
    route:         page.route,
    seq:           page.seq ?? idx * 10,
    isActive:      page.isActive ?? true,
    submoduleId:   page.submoduleId ?? null,
    pageAccessKey: page.pageCode ?? page.pageAccessKey ?? null,
  };
}

// Transforms a raw modules array into the menuConfig shape Sidebar expects.
// Icon is looked up from MODULE_ICON_MAP using the stable moduleCode —
// never from module name or numeric ID (both can change).
function normalizeMenuData(rawModules) {
  return {
    modules: rawModules.map((mod, modIdx) => ({
      id:           mod.id,
      moduleCode:   mod.moduleCode,
      name:         mod.name,
      icon:         MODULE_ICON_MAP[mod.moduleCode] || DEFAULT_MODULE_ICON,
      mode:         mod.mode         || "NORMAL",
      seq:          mod.seq          ?? modIdx * 10,
      isActive:     mod.isActive     ?? true,
      platformOnly: mod.platformOnly ?? false,
      clientOnly:   mod.clientOnly   ?? false,
      directPages:  (mod.directPages || []).map(normalizePage),
      submodules:   (mod.submodules  || []).map((sub, subIdx) => ({
        id:       sub.id,
        name:     sub.name,
        seq:      sub.seq ?? subIdx * 10,
        isActive: sub.isActive ?? true,
        pages:    (sub.pages || []).map(normalizePage),
      })),
    })),
  };
}

/**
 * Loads menu from the backend API using the logged-in user's applicationId.
 * Falls back to static config if the API call fails or returns no data.
 *
 * To activate the live API when the backend is ready:
 *   1. Uncomment the two lines marked ← ACTIVATE
 *   2. Remove (or comment out) the static-config dispatch below those lines
 */
export const loadMenuFromAPI = () => async (dispatch, getState) => {
  dispatch({ type: "MENU_LOADING_START" });

  const userState = getState().User;
  const applicationId = userState?.userDetails?.applicationId;

  try {
    // organizationId is attached automatically by the Http.js request
    // interceptor (as organizationIds) — don't pass it here too, or the
    // request ends up with both organizationId and organizationIds set.
    const res = await getMenu({ applicationId });
    const rawModules = res?.data?.modules || [];

    if (rawModules.length > 0) {
      // ← ACTIVATE: uncomment these 2 lines when the backend API is ready
      // const menuData = normalizeMenuData(rawModules);
      // dispatch({ type: SET_MENU_DATA, payload: menuData });
    }

    // Static config path — used until backend API is ready.
    // normalizeMenuData adds the icon field from MODULE_ICON_MAP[moduleCode].
    const { default: menuConfig } = require("../../config/menuConfig");
    dispatch({ type: SET_MENU_DATA, payload: normalizeMenuData(menuConfig.modules) });
  } catch (err) {
    console.error("Menu API failed, falling back to static config:", err);
    const { default: menuConfig } = require("../../config/menuConfig");
    dispatch({ type: SET_MENU_DATA, payload: normalizeMenuData(menuConfig.modules) });
  } finally {
    dispatch({ type: "MENU_LOADING_END" });
  }
};

// Loads menu directly from the static config (local dev / emergency fallback)
export const loadMenuFromConfig = () => (dispatch) => {
  const { default: menuConfig } = require("../../config/menuConfig");
  dispatch({ type: SET_MENU_DATA, payload: normalizeMenuData(menuConfig.modules) });
};

export const clearMenuData = () => ({ type: CLEAR_MENU_DATA });
